<template>
  <div class="base-input" :class="{ error: hasValidationError }">
    <label
      v-if="label"
      :class="['input__label', { readonly }]"
      @click="() => !readonly && focus()"
    >
      {{ label }}
      <span v-if="required">*</span>

      <VTooltip
        v-if="tooltip"
        :popup-text="tooltip"
        :show-tip="!!tooltip"
      >
        <svgicon
          class="svg-icon--input-info"
          icon="info"
          width="20"
          height="20"
        />
      </VTooltip>
    </label>

    <!-- READONLY -->
    <div v-if="readonly" class="input__field readonly-block">
      <div v-html="value" />
    </div>

    <!-- EDITOR -->
    <vue-editor
      v-else
      ref="input"
      v-model="localValue"
      class="input__field editor"
      :class="{ 'is-disabled': disabled }"
      :disabled="disabled"
      :editor-toolbar="toolbar"
      @blur="handleBlur"
      @focus="$emit('focus')"
      @input="handleInput"
    />

    <slot
      v-if="hasValidationError && !serverValidationPending"
      class="validation-messages"
    />
  </div>
</template>

<script>
import { VueEditor } from 'vue2-editor'
import VTooltip from '@/components/ui/VTooltip.vue'
import Base from './Base/Base.vue'
import { sanitizeHtml } from '@/utils/string'

export default {
  name: 'VEditor',
  components: { VueEditor, VTooltip },
  extends: Base,

  props: {
    value: String,
    label: String,
    name: String,
    placeholder: String,
    required: Boolean,
    readonly: Boolean,
    disabled: Boolean,
    tooltip: String,
    limitMaxLength: Number
  },

  mounted () {
    this.$nextTick(() => {
      this.setOffsetScroll()
    })
  },

  computed: {
    localValue: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', this.limit(val))
      }
    },

    toolbar () {
      return [
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ['clean']
      ]
    },

    rules () {
      const validators = {}
      const text = (this.value || '')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, '')
        .trim()

      if (this.required) {
        if (!text) {
          validators.required = () => false
        }
      }

      if (this.customValidation) {
        validators.customValidation = value => this.customValidation(value)
      }

      return validators
    }
  },

  methods: {
    handleBlur () {
      this.$emit('blur')
    },

    handleInput () {
      this.setOffsetScroll()
    },

    focus () {
      const quill = this.$refs.input && this.$refs.input.quill
      if (quill) {
        quill.focus()
      }
    },

    limit (html) {
      const cleanHtml = sanitizeHtml(html)
      if (!this.limitMaxLength) return cleanHtml

      const text = cleanHtml.replace(/<[^>]*>/g, '')
      return text.length > this.limitMaxLength ? this.value : cleanHtml
    },

    setOffsetScroll () {
      const el = this.$refs.input?.$el
      const container = el?.querySelector('.ql-container')
      if (!container) return

      const check = () => {
        if (container.offsetHeight > 0) {
          if (container.scrollHeight > container.clientHeight) container.classList.add('has-scroll')
          else container.classList.remove('has-scroll') // вызываем, когда размеры реальные
        } else {
          requestAnimationFrame(check) // ждём следующий кадр
        }
      }

      check()
    }
  }
}
</script>

<style lang="scss" scoped>
.editor {
  display: flex;
  flex-direction: column;
  background: var(--color__white);
  height: auto;
  min-height: 110px;
  max-height: 560px;
  padding: 0;
  //overflow: hidden;

  &.is-disabled {
    background: var(--color__lavander);
    color: $color__gray;

    &::v-deep .ql-toolbar {
      display: none;
    }
  }

  @media screen and (max-width: $tablet-width-s) {
    max-height: 300px;
  }

  &::v-deep .ql-container {
    flex: 1 1 auto;
    font-family: inherit;
    min-height: 0;
    box-sizing: border-box;
    overflow-y: auto;
    margin: 15px 0;

    &.has-scroll {
      margin-right: 15px;
    }

    &.ql-snow {
      border: none;
    }
  }

  &::v-deep .ql-toolbar {
    flex: 0 0 auto;

    &.ql-snow {
      border: none;
      border-bottom: 1px solid $color__periwinkle-crayola;

      @media screen and (max-width: $tablet-width-s) {
        border-color: var(--color__pearl-dark-gray);
      }

      .ql-formats {
        margin-bottom: 4px;
      }

       button.ql-active,
       button:focus,
       button:hover,
       .ql-picker-label.ql-active {
         color: $color__blue;

         .ql-stroke {
           stroke: $color__blue;
         }
       }
    }
  }

  &::v-deep .ql-editor {
    padding: 0 15px 0;
    min-height: 0;
    max-height: 100%;
    word-break: break-word;
  }
}
</style>
